import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Banner, Button, EmptyState } from '@/components/ds-zta';
import { apiErrorMessage, isNotFoundError } from '@/lib/apiError';

// Design evaluation F4: a detail route whose load fails must say so, not claim
// the record does not exist. A 404 is the one case that really is "not found";
// anything else (403, 5xx, timeout, network) is an error with a Retry.

interface DetailLoadErrorProps {
  /** Lower-case noun: "user", "group", "role". */
  entity: string;
  error: unknown;
  icon: React.ReactNode;
  onRetry: () => void;
  backLabel: string;
  onBack: () => void;
}

export const DetailLoadError: React.FC<DetailLoadErrorProps> = ({ entity, error, icon, onRetry, backLabel, onBack }) => {
  const back = (
    <Button size="sm" variant="secondary" onClick={onBack}>
      {backLabel}
    </Button>
  );
  if (!error || isNotFoundError(error)) {
    const Entity = entity.charAt(0).toUpperCase() + entity.slice(1);
    return (
      <EmptyState
        icon={icon}
        title={`${Entity} not found.`}
        description="It may have been deleted, or the link is wrong."
        action={back}
      />
    );
  }
  return (
    <EmptyState
      icon={<AlertTriangle />}
      title={`Couldn't load this ${entity}`}
      description={apiErrorMessage(error, `Something went wrong while loading this ${entity}.`)}
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>
            Retry
          </Button>
          {back}
        </div>
      }
    />
  );
};

/** In-panel error for a secondary list on a detail page (roles, members, groups). */
export const SectionLoadError: React.FC<{ what: string; error: unknown; onRetry: () => void }> = ({ what, error, onRetry }) => (
  <Banner
    variant="danger"
    title={`Couldn't load ${what}`}
    action={
      <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>
        Retry
      </Button>
    }
  >
    {apiErrorMessage(error, `Something went wrong while loading ${what}.`)}
  </Banner>
);
